import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { AuroraCachedApi } from '../services/aurora-cache';
import { t } from '../translations';
import type { LocationUpdateEvent } from '../app-index';
import type { Station } from '../services/aurora-api';

@customElement('app-globe')
export class AppGlobe extends LitElement {
  // The only station with an all-sky camera popup today.
  private static readonly ALLSKY_STATION_CODE = 'NAQ';

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private globe!: THREE.Mesh;
  private animationFrameId: number = 0;
  private controls!: OrbitControls;

  // Location marker for user position
  private locationMarker!: THREE.Mesh;

  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private tooltipElement: HTMLDivElement | null = null;

  private stationMarkers: { [key: string]: THREE.Mesh } = {};
  private stationPulses: { [code: string]: THREE.Mesh } = {};

  private resizeObserver?: ResizeObserver;
  private isTouchDevice: boolean = false;
  private isTooltipVisible: boolean = false;

  // Oval layers
  private hp30PolarOvalPoints?: THREE.Points;
  private polarElectroJetPoints?: THREE.Points;
  private auroraPoints?: THREE.Points;

  private auroraOpacity: number = 1.0;
  private polarElectroJetVisible: boolean = false;

  // Bound event handler references, created once so addEventListener and
  // removeEventListener always operate on the same function identity.
  private readonly boundOnWindowResize = this.onWindowResize.bind(this);
  private readonly boundHandleLocationUpdate = this.handleLocationUpdate.bind(
    this
  ) as EventListener;
  private readonly boundHandleToggleHp30AuroraOval =
    this.handleToggleHp30AuroraOval.bind(this) as EventListener;
  private readonly boundHandleToggleNoaaOvation =
    this.handleToggleNoaaOvation.bind(this) as EventListener;
  private readonly boundHandleTogglePolarElectroJet =
    this.handleTogglePolarElectroJet.bind(this) as EventListener;
  private readonly boundOnTouchStart = this.onTouchStart.bind(this);
  private readonly boundOnTouchEnd = this.onTouchEnd.bind(this);
  private readonly boundOnTouch = this.onTouch.bind(this);
  private readonly boundOnMouseMove = this.onMouseMove.bind(this);
  private readonly boundOnMouseClick = this.onMouseClick.bind(this);

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100vh;
      position: relative;
      background: #0d1726;
      /* Ensure background is always visible even if WebGL fails */
      min-height: 100vh;
      overflow: hidden;
    }

    canvas {
      width: 100%;
      height: 100%;
      display: block;
      /* Fallback background in case canvas fails to render */
      background: inherit;
    }

    /* Loading state indicator */
    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #0d1726;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 16px;
      z-index: 10;
      transition: opacity 0.3s ease-out;
    }

    .loading-overlay.hidden {
      opacity: 0;
      pointer-events: none;
    }

    .tooltip {
      position: fixed;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      font-size: 16px;
      pointer-events: none;
      z-index: 1000;
      display: none;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(4px);
      transition: opacity 0.2s ease-in-out;
    }

    .tooltip.touch-tooltip {
      pointer-events: auto;
      padding: 16px 20px;
      font-size: 18px;
      min-width: 120px;
      text-align: center;
    }

    /* Ensure proper layer stacking for better performance */
    :host([hidden]) {
      display: none !important;
    }
  `;

  constructor() {
    super();
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
    this.renderer.setClearColor(0x0a1420, 1); // or 0D1726

    // Detect touch capability
    this.isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  firstUpdated() {
    this.initScene();
    this.startAnimation();
    window.addEventListener('resize', this.boundOnWindowResize);
    this.addEventListener('location-update', this.boundHandleLocationUpdate);
    this.addEventListener(
      'toggle-aurora-oval',
      this.boundHandleToggleHp30AuroraOval
    );
    this.addEventListener(
      'toggle-noaa-ovation',
      this.boundHandleToggleNoaaOvation
    );
    this.addEventListener(
      'toggle-polar-electro-jet',
      this.boundHandleTogglePolarElectroJet
    );

    // Create tooltip element
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'tooltip';
    this.shadowRoot?.appendChild(this.tooltipElement);

    // Add touch event listeners for better touch support
    if (this.isTouchDevice) {
      this.renderer.domElement.addEventListener(
        'touchstart',
        this.boundOnTouchStart,
        { passive: false }
      );
      this.renderer.domElement.addEventListener(
        'touchend',
        this.boundOnTouchEnd,
        { passive: false }
      );
      // Prevent default click behavior on touch devices to avoid conflicts
      this.renderer.domElement.addEventListener('click', this.boundOnTouch);
    } else {
      // Add mouse move listeners for non-touch devices
      this.renderer.domElement.addEventListener(
        'mousemove',
        this.boundOnMouseMove
      );
      this.renderer.domElement.addEventListener(
        'click',
        this.boundOnMouseClick
      );
    }

    // Add ResizeObserver for more reliable sizing
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.onWindowResize());
      this.resizeObserver.observe(this);
    }

    // Force initial resize to ensure proper sizing
    requestAnimationFrame(() => this.onWindowResize());

    // Preload cache for better performance
    AuroraCachedApi.preloadCache().catch((error) => {
      console.warn('Failed to preload Aurora API cache:', error);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAnimation();
    window.removeEventListener('resize', this.boundOnWindowResize);
    this.removeEventListener('location-update', this.boundHandleLocationUpdate);
    this.removeEventListener(
      'toggle-aurora-oval',
      this.boundHandleToggleHp30AuroraOval
    );
    this.removeEventListener(
      'toggle-noaa-ovation',
      this.boundHandleToggleNoaaOvation
    );
    this.removeEventListener(
      'toggle-polar-electro-jet',
      this.boundHandleTogglePolarElectroJet
    );

    // Clean up touch event listeners
    if (this.isTouchDevice) {
      this.renderer.domElement.removeEventListener(
        'touchstart',
        this.boundOnTouchStart
      );
      this.renderer.domElement.removeEventListener(
        'touchend',
        this.boundOnTouchEnd
      );
      this.renderer.domElement.removeEventListener('click', this.boundOnTouch);
    } else {
      this.renderer.domElement.removeEventListener(
        'mousemove',
        this.boundOnMouseMove
      );
      this.renderer.domElement.removeEventListener(
        'click',
        this.boundOnMouseClick
      );
    }

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    if (this.tooltipElement && this.tooltipElement.parentNode) {
      this.tooltipElement.parentNode.removeChild(this.tooltipElement);
    }
  }

  private hideLoadingOverlay() {
    const loadingOverlay = this.shadowRoot?.getElementById('loading');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
      // Remove from DOM after animation completes
      setTimeout(() => {
        if (loadingOverlay.parentNode) {
          loadingOverlay.parentNode.removeChild(loadingOverlay);
        }
      }, 300);
    }
  }

  private async initScene() {
    // Set up renderer - use component dimensions instead of window dimensions
    const width = this.clientWidth || window.innerWidth;
    const height = this.clientHeight || window.innerHeight;

    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Cap pixel ratio for performance
    this.shadowRoot?.appendChild(this.renderer.domElement);

    // Set up camera with correct aspect ratio
    this.camera.aspect = width / height;
    this.camera.position.z = 0;
    this.camera.position.y = 5;
    this.camera.position.x = 1;
    this.camera.updateProjectionMatrix();

    // Set up OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.rotateSpeed = 0.5;
    this.controls.minDistance = 2.5;
    this.controls.maxDistance = 5;
    this.controls.autoRotate = false;
    this.controls.enablePan = false;
    this.controls.target.set(0, 0, 0);

    // Create basic globe first for immediate display
    await this.createBasicGlobe();

    // Initialize location markers
    this.initializeLocationMarkers();

    // Add lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.0005);
    this.scene.add(ambientLight);

    const subSolarPoint = this.calculateSunPosition();
    const directionalLight = new THREE.DirectionalLight(0xffffff, 3.9);
    directionalLight.position.copy(subSolarPoint);
    this.scene.add(directionalLight);

    this.updateSubSolarPoint = () => {
      console.log('Update sub solar point');
      const newSubSolarPoint = this.calculateSunPosition();
      directionalLight.position.copy(newSubSolarPoint);
    };

    // Hide loading overlay once basic globe is ready
    this.hideLoadingOverlay();

    // Load detailed textures and data progressively
    this.loadDetailedGlobeAsync();
    this.loadDataAsync();
  }

  private async createBasicGlobe() {
    // Create low-detail geometry for immediate display
    const geometry = new THREE.SphereGeometry(1, 64, 64);

    // Create basic material without textures for immediate display
    const basicMaterial = new THREE.MeshPhongMaterial({
      color: 0x1a4a5a, // Earth-like blue-green color
      shininess: 0.1,
    });

    // Create and add basic globe
    this.globe = new THREE.Mesh(geometry, basicMaterial);
    this.scene.add(this.globe);
  }

  private async loadDetailedGlobeAsync() {
    try {
      const textureLoader = new THREE.TextureLoader();

      // Load Earth textures in parallel
      const [earthDayTexture, earthNightTexture, bumpMap, specularMap] =
        await Promise.all([
          this.loadTexture(textureLoader, '/assets/earth/earth-day2.jpg'),
          this.loadTexture(textureLoader, '/assets/earth/earth-night2.jpg'),
          this.loadTexture(textureLoader, '/assets/earth/earth-topology.png'),
          this.loadTexture(textureLoader, '/assets/earth/earth-water.png'),
        ]);

      // Create detailed Earth material
      const earthMaterial = new THREE.MeshPhongMaterial({
        map: earthDayTexture,
        emissiveMap: earthNightTexture,
        bumpMap: bumpMap,
        bumpScale: 0.02,
        specularMap: specularMap,
        specular: new THREE.Color(0x000000),
        shininess: 0.6,
        emissive: new THREE.Color(0xffffff),
        emissiveIntensity: 0.2,
        opacity: 1.0,
        transparent: false,
      });

      // Replace the basic material with detailed one
      if (this.globe) {
        this.globe.material = earthMaterial;
      }
    } catch (error) {
      console.warn(
        'Failed to load detailed globe textures, keeping basic globe:',
        error
      );
    }
  }

  private async loadDataAsync() {
    try {
      // Load stations first (most important for functionality)
      await this.loadStations();
      await this.refreshStations();

      // Set up station refresh interval
      setInterval(() => {
        this.refreshStations();
      }, 180000);

      // Load polar oval data after a short delay
      setTimeout(() => {
        this.createHp30PolarOval().catch((error) => {
          console.warn('Failed to load polar oval:', error);
        });
      }, 1000);
    } catch (error) {
      console.warn('Failed to load some data layers:', error);
    }
  }

  private loadTexture(
    loader: THREE.TextureLoader,
    url: string
  ): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      loader.load(
        url,
        (texture) => {
          resolve(texture);
        },
        undefined,
        (error) => {
          console.error(`Error loading texture ${url}:`, error);
          reject(error);
        }
      );
    });
  }

  private initializeLocationMarkers() {
    const color = 0xff69b4;

    // Create location marker with larger, more visible size
    const markerGeometry = new THREE.SphereGeometry(0.01, 32, 32);
    const markerMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: false,
      opacity: 1.0,
    });
    this.locationMarker = new THREE.Mesh(markerGeometry, markerMaterial);
    this.locationMarker.visible = false;
    this.locationMarker.renderOrder = 100; // Render on top

    this.scene.add(this.locationMarker);
  }

  private async createHp30PolarOval() {
    try {
      const points = await AuroraCachedApi.getPolarOval();
      if (!points || points.length === 0) return;

      // remove existing layer if present
      if (this.hp30PolarOvalPoints) {
        try {
          this.scene.remove(this.hp30PolarOvalPoints);
          const geom = (this.hp30PolarOvalPoints as any).geometry;
          const mat = (this.hp30PolarOvalPoints as any).material;
          if (geom && geom.dispose) geom.dispose();
          if (mat && mat.dispose) mat.dispose();
        } catch (e) {
          /* ignore */
        }
        this.hp30PolarOvalPoints = undefined;
      }

      // Build attribute arrays
      const positions: number[] = [];
      const sizes: number[] = [];

      const radius = 1.05;
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const point = this.getPoint(p.lat, p.lon, radius);
        positions.push(point.x, point.y, point.z);

        // constant screen size for polar oval dots (pixels)
        sizes.push(30); // tweak this (6..18) to taste
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
      );
      geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));

      // create soft circular sprite texture
      const spriteSize = 200;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = spriteSize;
      const ctx = canvas.getContext('2d')!;
      const grad = ctx.createRadialGradient(
        spriteSize / 2,
        spriteSize / 2,
        0,
        spriteSize / 2,
        spriteSize / 2,
        spriteSize / 2
      );
      grad.addColorStop(0.0, 'rgba(255,255,255,1.0)');
      grad.addColorStop(0.25, 'rgba(255,255,255,0.9)');
      grad.addColorStop(0.6, 'rgba(255,255,255,0.25)');
      grad.addColorStop(1.0, 'rgba(255,255,255,0.0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, spriteSize, spriteSize);
      const spriteTex = new THREE.Texture(canvas);
      spriteTex.needsUpdate = true;
      spriteTex.minFilter = THREE.LinearFilter;
      spriteTex.magFilter = THREE.LinearFilter;

      // color for all oval dots (use same for all)
      const ovalColor = new THREE.Color(0xffffff); // change to desired hex

      const material = new THREE.ShaderMaterial({
        uniforms: {
          pointTexture: { value: spriteTex },
          pixelRatio: { value: Math.min(window.devicePixelRatio, 1) },
          sizeMultiplier: { value: 2.0 }, // multiply per-point 'size' (in pixels)
          uColor: { value: ovalColor },
          globalOpacity: { value: 0.025 }, // original thin opacity; adjust as needed
        },
        vertexShader: `
          precision mediump float;
          uniform float pixelRatio;
          uniform float sizeMultiplier;
          attribute float size;
          varying float vSize;
          void main() {
            vSize = size;
            // vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            // // Use constant screen-space size (pixels) so dots don't swell when zooming out.
            // // size attribute is interpreted as pixels; sizeMultiplier scales globally.
            // gl_PointSize = size * pixelRatio * sizeMultiplier;
            // gl_Position = projectionMatrix * mvPosition;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            // distance-based attenuation so points scale with distance to camera/globe
            float dist = length(mvPosition.xyz);
            float atten = 1.0 / (1.0 + 0.2 * dist); // tweak 0.02 to taste
            gl_PointSize = size * pixelRatio * sizeMultiplier * atten;
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          precision mediump float;
          uniform sampler2D pointTexture;
          uniform vec3 uColor;
          uniform float globalOpacity;
          varying float vSize;
          void main() {
            vec4 tex = texture2D(pointTexture, gl_PointCoord);
            float alpha = tex.a * globalOpacity;
            if (alpha < 0.01) discard;
            vec3 col = uColor * tex.rgb;
            gl_FragColor = vec4(col, alpha);
          }
        `,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        blending: THREE.NormalBlending,
      });

      this.hp30PolarOvalPoints = new THREE.Points(geometry, material);
      this.hp30PolarOvalPoints.renderOrder = 10;
      this.hp30PolarOvalPoints.visible = true;

      this.scene.add(this.hp30PolarOvalPoints);
    } catch (error) {
      console.error('Error creating polar oval:', error);
    }
  }

  private async loadOvationAuroraData() {
    try {
      const coords = await AuroraCachedApi.getOvationAurora();

      if (coords.length === 0) {
        console.warn('No Ovation Aurora data available');
        return;
      }
      // Debug: check longitude/latitude ranges and convention ([-180..180] vs [0..360])
      const lonVals = coords.map((c: any) => c.longitude);
      const latVals = coords.map((c: any) => c.latitude);
      const minLon = Math.min(...lonVals);
      const maxLon = Math.max(...lonVals);
      const minLat = Math.min(...latVals);
      const maxLat = Math.max(...latVals);
      console.log(
        'Ovation lon range:',
        minLon,
        'to',
        maxLon,
        '  lat range:',
        minLat,
        'to',
        maxLat
      );
      if (maxLon > 180) {
        console.log('Ovation longitudes appear to be in 0..360 range');
      } else {
        console.log('Ovation longitudes appear to be in -180..180 range');
      }
      // Find min/max aurora values for scaling
      const auroraValues = coords.map((c) => c.auroraValue);
      const minAurora = Math.min(...auroraValues);
      const maxAurora = Math.max(...auroraValues);
      console.log(
        'Aurora value min (data):',
        minAurora,
        'max (data):',
        maxAurora
      );
      // Fixed display range (maps c[2] -> color/opacity/size)
      const displayMin = 3;
      const displayMax = 100;

      // Color stops (will be interpolated)
      const colorStops = [
        { v: displayMin, color: [39, 128, 151], a: 0.1 },
        { v: 5, color: [39, 128, 151], a: 0.2 },
        { v: 20, color: [39, 128, 151], a: 0.3 },
        { v: 30, color: [24, 225, 211], a: 0.35 },
        { v: 40, color: [24, 225, 211], a: 0.5 },
        { v: 50, color: [255, 180, 115], a: 0.6 },
        { v: 60, color: [239, 91, 40], a: 0.7 },
        { v: 70, color: [239, 91, 40], a: 0.8 },
        { v: 90, color: [229, 0, 0], a: 0.9 },
        { v: displayMax, color: [229, 0, 0], a: 0.9 },
      ];

      const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

      // safe minimum for final dot alpha (tweak as needed)
      const MIN_FINAL_OPACITY = 0.05;

      function getHeatmapColor(value: number): [number, number] {
        // clamp to display range for color interpolation, but keep raw value for special cases
        const v = Math.max(displayMin, Math.min(displayMax, value));

        // find surrounding stops for color interpolation
        let lower = colorStops[0];
        let upper = colorStops[colorStops.length - 1];
        for (let i = 0; i < colorStops.length - 1; i++) {
          if (v >= colorStops[i].v && v <= colorStops[i + 1].v) {
            lower = colorStops[i];
            upper = colorStops[i + 1];
            break;
          }
        }
        const span = upper.v - lower.v || 1;
        const t = (v - lower.v) / span;
        const r = Math.round(lerp(lower.color[0], upper.color[0], t));
        const g = Math.round(lerp(lower.color[1], upper.color[1], t));
        const b = Math.round(lerp(lower.color[2], upper.color[2], t));
        const color = (r << 16) | (g << 8) | b;

        // Opacity handling:
        // - Special-case lower values (example: 5..9)
        let a: number;
        if (value >= 5 && value <= 9) {
          a = 0.1; // fixed low opacity for range 5..9 (tweak as desired)
        } else {
          // linear mapping for other values (adjust min/max as before)
          const minDotOpacity = 0.1;
          const maxDotOpacity = 0.8;
          const norm = (v - displayMin) / (displayMax - displayMin); // 0..1
          a = Math.max(
            0,
            Math.min(1, lerp(minDotOpacity, maxDotOpacity, norm))
          );
        }

        // Enforce a guaranteed minimum so no point becomes effectively invisible
        a = Math.max(lower.a, MIN_FINAL_OPACITY);

        return [color, a];
      }

      if (this.auroraPoints) {
        this.scene.remove(this.auroraPoints);
      }

      // Build attribute arrays
      const positions: number[] = [];
      const colors: number[] = [];
      const sizes: number[] = [];
      const opacities: number[] = [];

      coords.forEach((coord) => {
        const radius = 1.02;
        const point = this.getPoint(coord.latitude, coord.longitude, radius);

        positions.push(point.x, point.y, point.z);

        const [colorHex, opacity] = getHeatmapColor(coord.auroraValue);
        const r = ((colorHex >> 16) & 0xff) / 255;
        const g = ((colorHex >> 8) & 0xff) / 255;
        const b = (colorHex & 0xff) / 255;
        colors.push(r, g, b);

        // size in pixels (tune min/max)
        const minSize = 2;
        const maxSize = 3;
        const norm =
          (Math.max(displayMin, Math.min(displayMax, coord.auroraValue)) -
            displayMin) /
          (displayMax - displayMin);
        sizes.push(minSize + (maxSize - minSize) * norm);

        opacities.push(opacity);
      });

      // create BufferGeometry and attributes
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
      );
      geometry.setAttribute(
        'customColor',
        new THREE.Float32BufferAttribute(colors, 3)
      );
      geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));
      geometry.setAttribute(
        'opacity',
        new THREE.Float32BufferAttribute(opacities, 1)
      );

      // create sprite texture (radial soft circle) inline
      const spriteSize = 128;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = spriteSize;
      const ctx = canvas.getContext('2d')!;
      const grad = ctx.createRadialGradient(
        spriteSize / 2,
        spriteSize / 2,
        0,
        spriteSize / 2,
        spriteSize / 2,
        spriteSize / 2
      );
      grad.addColorStop(0.0, 'rgba(255,255,255,1.0)');
      grad.addColorStop(0.25, 'rgba(255,255,255,0.9)');
      grad.addColorStop(0.6, 'rgba(255,255,255,0.25)');
      grad.addColorStop(1.0, 'rgba(255,255,255,0.0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, spriteSize, spriteSize);
      const spriteTex = new THREE.Texture(canvas);
      spriteTex.needsUpdate = true;
      spriteTex.minFilter = THREE.LinearFilter;
      spriteTex.magFilter = THREE.LinearFilter;

      // ShaderMaterial for Points
      const material = new THREE.ShaderMaterial({
        uniforms: {
          pointTexture: { value: spriteTex },
          pixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
          sizeMultiplier: { value: 1.0 },
          globalOpacity: { value: this.auroraOpacity },
        },
        vertexShader: `
          precision mediump float;
          uniform float pixelRatio;
          uniform float sizeMultiplier;
          attribute float size;
          attribute vec3 customColor;
          attribute float opacity;
          varying vec3 vColor;
          varying float vOpacity;
          void main() {
            vColor = customColor;
            vOpacity = opacity;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            // adjust attenuation constant if needed for your camera distance
            gl_PointSize = size * (200.0 / -mvPosition.z) * pixelRatio * sizeMultiplier;
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          precision mediump float;
          uniform sampler2D pointTexture;
          uniform float globalOpacity;
          varying vec3 vColor;
          varying float vOpacity;
          void main() {
            vec4 tex = texture2D(pointTexture, gl_PointCoord);
            float alpha = tex.a * vOpacity * globalOpacity;
            if (alpha < 0.02) discard;
            gl_FragColor = vec4(vColor, alpha);
          }
        `,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        blending: THREE.NormalBlending,
        vertexColors: true,
      });

      this.auroraPoints = new THREE.Points(geometry, material);
      this.auroraPoints.renderOrder = 20;

      this.scene.add(this.auroraPoints);
      (material.uniforms.sizeMultiplier as any).value = 0.15;
    } catch (error) {
      console.error('Error loading Ovation Aurora data:', error);
    }
  }

  private async loadPolarElectroJetData() {
    try {
      const points = await AuroraCachedApi.getPolarElectroJet();
      if (!points || points.length === 0) return;

      // remove existing layer if present
      if (this.polarElectroJetPoints) {
        try {
          this.scene.remove(this.polarElectroJetPoints);
          const geom = (this.polarElectroJetPoints as any).geometry;
          const mat = (this.polarElectroJetPoints as any).material;
          if (geom && geom.dispose) geom.dispose();
          if (mat && mat.dispose) mat.dispose();
        } catch (e) {
          /* ignore */
        }
        this.polarElectroJetPoints = undefined;
      }

      // Build attribute arrays
      const positions: number[] = [];
      const sizes: number[] = [];

      const radius = 1.05;
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const phi = (90 - p.lat) * (Math.PI / 180);
        const theta = (p.lon - 180) * (Math.PI / 180);

        const x = -(radius * Math.sin(phi) * Math.cos(theta));
        const y = radius * Math.cos(phi);
        const z = radius * Math.sin(phi) * Math.sin(theta);

        positions.push(x, y, z);

        // constant screen size for polar electro jet dots (pixels)
        sizes.push(30); // same as polar oval
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
      );
      geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));

      // create soft circular sprite texture
      const spriteSize = 200;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = spriteSize;
      const ctx = canvas.getContext('2d')!;
      const grad = ctx.createRadialGradient(
        spriteSize / 2,
        spriteSize / 2,
        0,
        spriteSize / 2,
        spriteSize / 2,
        spriteSize / 2
      );
      grad.addColorStop(0.0, 'rgba(255,255,255,1.0)');
      grad.addColorStop(0.25, 'rgba(255,255,255,0.9)');
      grad.addColorStop(0.6, 'rgba(255,255,255,0.25)');
      grad.addColorStop(1.0, 'rgba(255,255,255,0.0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, spriteSize, spriteSize);
      const spriteTex = new THREE.Texture(canvas);
      spriteTex.needsUpdate = true;
      spriteTex.minFilter = THREE.LinearFilter;
      spriteTex.magFilter = THREE.LinearFilter;

      // color for all electro jet dots
      const jetColor = new THREE.Color(0xffffff);

      const material = new THREE.ShaderMaterial({
        uniforms: {
          pointTexture: { value: spriteTex },
          pixelRatio: { value: Math.min(window.devicePixelRatio, 1) },
          sizeMultiplier: { value: 2.0 }, // multiply per-point 'size' (in pixels)
          uColor: { value: jetColor },
          globalOpacity: { value: 0.015 }, // same thin opacity as polar oval
        },
        vertexShader: `
          precision mediump float;
          uniform float pixelRatio;
          uniform float sizeMultiplier;
          attribute float size;
          varying float vSize;
          void main() {
            vSize = size;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            // distance-based attenuation so points scale with distance to camera/globe
            float dist = length(mvPosition.xyz);
            float atten = 1.0 / (1.0 + 0.2 * dist);
            gl_PointSize = size * pixelRatio * sizeMultiplier * atten;
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          precision mediump float;
          uniform sampler2D pointTexture;
          uniform vec3 uColor;
          uniform float globalOpacity;
          varying float vSize;
          void main() {
            vec4 tex = texture2D(pointTexture, gl_PointCoord);
            float alpha = tex.a * globalOpacity;
            if (alpha < 0.01) discard;
            vec3 col = uColor * tex.rgb;
            gl_FragColor = vec4(col, alpha);
          }
        `,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        blending: THREE.NormalBlending,
      });

      this.polarElectroJetPoints = new THREE.Points(geometry, material);
      this.polarElectroJetPoints.renderOrder = 25; // Render above NOAA Ovation (20) and polar oval (10)
      this.polarElectroJetPoints.visible = this.polarElectroJetVisible;

      this.scene.add(this.polarElectroJetPoints);
    } catch (error) {
      console.error('Error loading Polar Electro Jet data:', error);
    }
  }

  private getPoint(lat: number, long: number, radius: number): THREE.Vector3 {
    // convert to colatitude and radians
    const phi = (90 - lat) * (Math.PI / 180);
    // convert to radians
    const theta = long * (Math.PI / 180);

    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.cos(phi);
    const z = -(radius * Math.sin(phi) * Math.sin(theta));

    return new THREE.Vector3(x, y, z);
  }

  private async loadStations() {
    try {
      const stations = await AuroraCachedApi.getStations();
      stations.forEach((station) => {
        const { lat, long } = station.coordinates;
        const r = 1.0;
        const point = this.getPoint(lat, long, r);

        // Add the station marker as a 2D circle with touch-optimized size
        const markerSize = this.isTouchDevice ? 0.022 : 0.015; // Larger for touch devices
        const markerGeometry = new THREE.CircleGeometry(markerSize, 32);
        const markerMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.0,
          side: THREE.DoubleSide, // So it's visible from both sides
        });
        const marker = new THREE.Mesh(markerGeometry, markerMaterial);
        marker.position.set(point.x, point.y, point.z);
        // Orient the marker to face outward from the globe
        marker.lookAt(new THREE.Vector3(0, 0, 0));
        marker.userData.name = station.name;
        marker.userData.code = station.code;
        marker.renderOrder = 100; //
        this.scene.add(marker);
        this.stationMarkers[station.code] = marker;

        const pulseGeometry = new THREE.CircleGeometry(0.01, 32);
        const pulseMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffff, // Start color, will update later
          transparent: false,
          opacity: 0.0,
          side: THREE.DoubleSide,
        });
        const pulse = new THREE.Mesh(pulseGeometry, pulseMaterial);
        pulse.position.set(point.x, point.y, point.z);
        pulse.lookAt(new THREE.Vector3(0, 0, 0));
        pulse.renderOrder = 30; // Render below the marker
        this.scene.add(pulse);
        this.stationPulses[station.code] = pulse;
      });
    } catch (error) {
      console.error('Error loading stations:', error);
    }
  }

  private static readonly STALE_MAGDATA_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

  // Station timestamps omit a timezone suffix (e.g. "2026-08-28T07:08:53") but
  // are always UTC; append "Z" so the browser's local timezone doesn't skew
  // the staleness check.
  private static parseStationTimestamp(timestamp: string): Date {
    return new Date(`${timestamp}Z`);
  }

  private updateStationPulses(stations: Station[]) {
    stations.forEach((station) => {
      const code = station.code;
      const pulse = this.stationPulses[code];
      if (!pulse) return;

      // Calculate dH
      var h = station.magdata?.h ?? 0;
      const quietH = station.quiet_level?.h ?? 0;

      const timestamp = station.magdata?.timestamp;
      const isStale =
        !timestamp ||
        Date.now() - AppGlobe.parseStationTimestamp(timestamp).getTime() >
          AppGlobe.STALE_MAGDATA_THRESHOLD_MS;

      // Treat missing quiet level or stale/missing magdata as data missing (i.e., h=1.0)
      if (quietH === 0 || isStale) {
        h = 1.0;
      }

      let dH = h - quietH;

      // Ensure dH is 1 if h is 1.0 (no data)
      if (h === 1.0) {
        dH = 1;
      }

      // Calculate base scale
      let baseScale = 1; // Default scale
      let color = 0x278097; // Default color
      let opacity = 1.0; // Default opacity
      // Set color and base scale based on dH
      if (Math.abs(dH) === 1) {
        color = 0x666666; // Grey for no activity
        baseScale = 0.5;
        opacity = 1.0;
        pulse.userData.noPulse = true;
      } else if (Math.abs(dH) >= 1 && Math.abs(dH) < 70) {
        color = 0x278097;
        baseScale = 1;
        opacity = 1.0;
        pulse.userData.noPulse = false;
      } else if (Math.abs(dH) >= 70 && Math.abs(dH) < 100) {
        color = 0x17e1d4;
        baseScale = 1.3;
        opacity = 1.0;
        pulse.userData.noPulse = false;
      } else if (Math.abs(dH) >= 100 && Math.abs(dH) < 250) {
        color = 0xffb473;
        baseScale = 1.5;
        opacity = 1.0;
        pulse.userData.noPulse = false;
      } else if (Math.abs(dH) >= 250 && Math.abs(dH) < 500) {
        color = 0xef5b28; // Bright red for extreme activity
        baseScale = 2;
        opacity = 1.0;
        pulse.userData.noPulse = false;
      } else if (Math.abs(dH) >= 500) {
        color = 0xe50000; // Bright red for extreme activity
        baseScale = 3;
        opacity = 1.0;
        pulse.userData.noPulse = false;
      }

      // Update pulse properties
      (pulse.material as THREE.MeshBasicMaterial).color.setHex(color);
      (pulse.material as THREE.MeshBasicMaterial).opacity = 1.0;
      pulse.userData.baseScale = baseScale; // Store base scale in userData
      pulse.renderOrder = 80;
    });
  }

  private async refreshStations() {
    const stations = await AuroraCachedApi.getStations();
    this.updateStationPulses(stations);
  }

  private showPopup() {
    // Remove existing popup if present
    const oldPopup = document.getElementById('station-popup');
    if (oldPopup) oldPopup.remove();

    // Create popup container
    const popup = document.createElement('div');
    popup.id = 'station-popup';
    popup.style.position = 'fixed';
    popup.style.left = '50%';
    popup.style.top = '50%';
    popup.style.transform = 'translate(-50%, -50%)';
    popup.style.background = '#0D1726';
    popup.style.padding = '16px';
    popup.style.borderRadius = '8px';
    popup.style.boxShadow = '0 2px 16px rgba(0,0,0,0.5)';
    popup.style.zIndex = '1000';

    // Add image
    const img = document.createElement('img');
    const allskyCode = AppGlobe.ALLSKY_STATION_CODE;
    img.src = `https://www.spacecenter.dk/files/data/Greenland-magnetometers/allsky/${allskyCode}/skyi_${allskyCode}_latest.jpg`;
    img.style.maxWidth = '400px';
    img.style.maxHeight = '400px';
    img.style.display = 'block';
    img.style.margin = '0 auto 12px auto';
    popup.appendChild(img);

    // Add close button
    const btn = document.createElement('button');
    btn.textContent = t('close');
    btn.style.display = 'block';
    btn.style.margin = '0 auto';
    btn.style.background = '#17556D'; // Button background color
    btn.style.color = '#FFFFFF'; // Button text color
    btn.onclick = () => popup.remove();
    popup.appendChild(btn);

    document.body.appendChild(popup);
  }

  private handleLocationUpdate(event: LocationUpdateEvent) {
    const { latitude, longitude } = event.detail;
    this.updateLocationMarker(latitude, longitude);
  }

  private handleToggleHp30AuroraOval(event: CustomEvent) {
    const { show } = event.detail;
    if (this.hp30PolarOvalPoints) {
      this.hp30PolarOvalPoints.visible = show;
    }
  }

  private handleToggleNoaaOvation(event: CustomEvent) {
    const { show } = event.detail;
    if (this.auroraPoints) {
      this.auroraPoints.visible = show;
    }

    if (show && !this.auroraPoints) {
      this.loadOvationAuroraData().catch((error) => {
        console.warn('Failed to load NOAA Ovation data:', error);
      });
    }
  }

  private handleTogglePolarElectroJet(event: CustomEvent) {
    const { show } = event.detail;
    this.polarElectroJetVisible = show;

    if (this.polarElectroJetPoints) {
      this.polarElectroJetPoints.visible = show;
    }

    // Load data if showing for the first time
    if (show && !this.polarElectroJetPoints) {
      this.loadPolarElectroJetData().catch((error) => {
        console.warn('Failed to load Polar Electro Jet data:', error);
      });
    }
  }

  private updateLocationMarker(latitude: number, longitude: number) {
    console.log(
      'Updating location marker to lat:',
      latitude,
      'lon:',
      longitude
    );

    if (!this.locationMarker) {
      console.error('Location markers not initialized');
      return;
    }

    // Convert lat/long to 3D coordinates on the globe surface
    const point = this.getPoint(latitude, longitude, 1.0);
    this.locationMarker.position.copy(point);
    this.locationMarker.visible = true;
  }

  private updateSubSolarPoint: () => void = () => {};
  private lastSunUpdate: number = 0;

  private startAnimation() {
    const animate = () => {
      this.animationFrameId = requestAnimationFrame(animate);
      this.controls.update();

      // Update sun position every 5 minutes
      const now = Date.now();
      if (now - this.lastSunUpdate > 5 * 60 * 1000) {
        // 5 minutes in milliseconds
        this.updateSubSolarPoint();
        this.lastSunUpdate = now;
      }

      // Animate station pulses
      const time = Date.now() * 0.003; // Adjust speed of pulsation
      Object.values(this.stationPulses).forEach((pulse) => {
        const baseScale = pulse.userData.baseScale || 1; // Base scale from dH
        if (pulse.userData && pulse.userData.noPulse) {
          pulse.scale.set(baseScale, baseScale, 1);
        } else {
          const pulsation = Math.sin(time) * 0.2; // Pulsation factor
          const currentScale = baseScale + pulsation;
          pulse.scale.set(currentScale, currentScale, 1);
        }
      });

      this.renderer.render(this.scene, this.camera);
    };

    animate();
  }

  private stopAnimation() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = 0;
    }
  }

  private onWindowResize() {
    const width = this.clientWidth || this.offsetWidth || window.innerWidth;
    const height = this.clientHeight || this.offsetHeight || window.innerHeight;

    // Only resize if we have valid dimensions
    if (width > 0 && height > 0) {
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }
  }

  private showTooltip(
    stationName: string,
    x: number,
    y: number,
    isTouch: boolean = false
  ) {
    if (this.tooltipElement) {
      this.tooltipElement.textContent = stationName;
      this.tooltipElement.style.display = 'block';

      if (isTouch) {
        this.tooltipElement.className = 'tooltip touch-tooltip';
        // Center the tooltip on touch
        this.tooltipElement.style.left = `${x - this.tooltipElement.offsetWidth / 2}px`;
        this.tooltipElement.style.top = `${y - this.tooltipElement.offsetHeight - 20}px`;
        this.isTooltipVisible = true;

        // Auto-hide after 3 seconds on touch devices
        setTimeout(() => {
          this.hideTooltip();
        }, 3000);
      } else {
        this.tooltipElement.className = 'tooltip';
        this.tooltipElement.style.left = `${x + 10}px`;
        this.tooltipElement.style.top = `${y + 10}px`;
      }
    }
  }

  private hideTooltip() {
    if (this.tooltipElement) {
      this.tooltipElement.style.display = 'none';
      this.isTooltipVisible = false;
    }
  }

  private onMouseMove(event: MouseEvent) {
    // Skip mouse interactions on touch devices to avoid conflicts
    if (this.isTouchDevice) return;

    // Calculate mouse position in normalized device coordinates relative to the component
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Update the picking ray with the camera and mouse position
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Get all station markers
    const markers = Object.values(this.stationMarkers);

    // Find intersections
    const intersects = this.raycaster.intersectObjects(markers);

    if (intersects.length > 0) {
      const station = intersects[0].object as THREE.Mesh;
      const name = station.userData.name;

      // Show tooltip
      this.showTooltip(name, event.clientX, event.clientY, false);

      // Highlight the marker
      (station.material as THREE.MeshBasicMaterial).color.set(0xe94c1f);
    } else {
      // Hide tooltip
      this.hideTooltip();

      // Reset all markers to original color
      markers.forEach((marker) => {
        (marker.material as THREE.MeshBasicMaterial).color.set(0xffffff);
      });
    }
  }

  private onTouchStart() {
    // Hide any existing tooltip when a new touch starts
    if (this.isTooltipVisible) {
      this.hideTooltip();
    }
  }

  private onTouchEnd(event: TouchEvent) {
    // Prevent default to avoid triggering mouse events
    event.preventDefault();

    if (event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      this.handleStationInteraction(touch.clientX, touch.clientY, true);
    }
  }

  private onTouch(event: MouseEvent) {
    // Handle the synthesized click event on touch devices
    event.preventDefault();
    this.handleStationInteraction(event.clientX, event.clientY, true);
  }

  private onMouseClick(event: MouseEvent) {
    // Handle click on non-touch devices
    this.handleStationInteraction(event.clientX, event.clientY, false);
  }

  private handleStationInteraction(
    clientX: number,
    clientY: number,
    isTouch: boolean
  ) {
    // Calculate position in normalized device coordinates
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2();
    mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    // Update the picking ray
    this.raycaster.setFromCamera(mouse, this.camera);

    // Get all station markers
    const markers = Object.values(this.stationMarkers);

    // Find intersections with larger threshold for touch
    const threshold = isTouch ? 0.02 : 0.01;
    this.raycaster.params.Points!.threshold = threshold;

    const intersects = this.raycaster.intersectObjects(markers);

    if (intersects.length > 0) {
      const station = intersects[0].object as THREE.Mesh;
      const name = station.userData.name;

      // Hide any existing tooltip first
      this.hideTooltip();

      // Show tooltip with appropriate positioning
      this.showTooltip(name, clientX, clientY, isTouch);

      // Highlight the marker temporarily
      (station.material as THREE.MeshBasicMaterial).color.set(0xe94c1f);

      // Reset color after delay
      setTimeout(
        () => {
          (station.material as THREE.MeshBasicMaterial).color.set(0xffffff);
        },
        isTouch ? 3000 : 500
      );

      // Special handling for Narsarsuaq Geomagnetic Observatory
      if (station.userData.code === AppGlobe.ALLSKY_STATION_CODE) {
        this.showPopup();
      }
    } else if (isTouch && this.isTooltipVisible) {
      // Hide tooltip when tapping elsewhere on touch devices
      this.hideTooltip();
    }
  }

  private calculateSunPosition(date: Date = new Date()): THREE.Vector3 {
    // Get UTC time as decimal hours
    const utcHours = date.getUTCHours();
    const utcMinutes = date.getUTCMinutes();
    const utcSeconds = date.getUTCSeconds();
    const utcDecimal = utcHours + utcMinutes / 60 + utcSeconds / 3600;

    // Calculate day of year (Jan 1 = 1)
    // Month is 0-indexed and date is 1-indexed
    const start = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay) + 1;

    // Declination (latitude of subsolar point)
    const decl = 23.44 * Math.sin(((2 * Math.PI) / 365) * (dayOfYear - 81));
    const declRad = (decl * Math.PI) / 180;

    // Correct subsolar longitude
    const subsolarLon = 180 - utcDecimal * 15;
    const point = this.getPoint(decl, subsolarLon, 5);

    return new THREE.Vector3(point.x, point.y, point.z)
      .normalize()
      .multiplyScalar(5);
  }

  render() {
    return html`
      <div class="loading-overlay" id="loading">Loading globe...</div>
      <div class="globe-container"></div>
    `;
  }
}
